/**
 * 材料から計算（試用）— 公式値の入力画面の中で、材料と重量から 1 食あたりの栄養成分を出す。
 *
 * - 成分は文部科学省「日本食品標準成分表（八訂）増補」（計算はサーバ・`src/domain/recipe.ts`）。配合は kalori の外に出ない
 * - 計算結果は**ボタンを押したときだけ**上の入力欄に入る（勝手に書き換えない）。根拠は「レシピから計算」に揃える
 * - kalori の推定から始めた場合は根拠を「推定」にしておく — 推定の重量のまま申請すると、AI の値が「レシピから計算」を
 *   名乗ることになる（入力欄を掲載値で埋めないのと同じ理由・merchant-api AGENTS.md）
 * - ⚠️ 新しい機能は画面に「試用」と出す（料金の線・ROADMAP §🎯）
 */
import { useEffect, useMemo, useState } from "react";
import { api, type IngredientHit, type PhotoEstimate, type RecipeCalc } from "../api";
import { toJpeg } from "../image";
import { Link } from "../router";
import { NUTRITION_FIELDS, fmtNumber } from "../format";

interface Row {
  id: string;
  name: string;
  grams: string;
}

/** 保存済みの計算（台帳の components）を画面の行に戻す。形 = `recipeComponents`（サーバ） */
export function savedRecipe(components: unknown): { rows: Row[]; servings: number; finishedGrams: number | null } | null {
  if (!Array.isArray(components)) return null;
  const c = components[0] as { source?: unknown; servings?: unknown; ingredients?: unknown; finishedGrams?: unknown } | undefined;
  if (!c || c.source !== "recipe_calculator" || !Array.isArray(c.ingredients)) return null;
  const servings = typeof c.servings === "number" && c.servings > 0 ? c.servings : 1;
  const finishedGrams = typeof c.finishedGrams === "number" && c.finishedGrams > 0 ? c.finishedGrams : null;
  const rows = (c.ingredients as { id?: unknown; name?: unknown; grams?: unknown }[])
    .filter((i) => typeof i.id === "string" && typeof i.grams === "number")
    .map((i) => ({ id: i.id as string, name: typeof i.name === "string" ? i.name : (i.id as string), grams: String(Math.round((i.grams as number) * servings * 10) / 10) }));
  return { rows, servings, finishedGrams };
}

/** kalori の推定（推定した食材構成）を行に変える。`ingredients[].grams` は 1 単位あたりなので数量を掛ける */
function estimateRows(components: unknown): { id: string; grams: number }[] {
  if (!Array.isArray(components)) return [];
  const out: { id: string; grams: number }[] = [];
  for (const c of components as { quantity?: unknown; ingredients?: unknown }[]) {
    const q = typeof c.quantity === "number" && c.quantity > 0 ? c.quantity : 1;
    if (!Array.isArray(c.ingredients)) continue;
    for (const i of c.ingredients as { id?: unknown; grams?: unknown }[]) {
      if (typeof i.id === "string" && typeof i.grams === "number" && i.grams > 0) out.push({ id: i.id, grams: Math.round(i.grams * q * 10) / 10 });
    }
  }
  return out;
}

function toGrams(raw: string): number | null {
  const s = raw.replace(/[０-９．]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0)).replace(/[,，\s]/g, "");
  if (!/^\d+(\.\d+)?$/.test(s)) return null;
  const n = Number(s);
  return n > 0 ? n : null;
}

export function RecipeCalculator({
  entryName,
  saved,
  estimate,
  editable,
  onApply,
}: {
  entryName: string;
  /** 台帳に保存済みの計算（あれば開いた状態で戻す） */
  saved: unknown;
  /** kalori の推定の食材構成（掲載中の商品の estimation.components） */
  estimate: unknown;
  editable: boolean;
  onApply: (calc: RecipeCalc, fromEstimate: boolean) => void;
}) {
  const initial = useMemo(() => savedRecipe(saved), [saved]);
  const [rows, setRows] = useState<Row[]>(initial?.rows ?? []);
  const [servings, setServings] = useState(String(initial?.servings ?? 1));
  /** 仕上がり重量（レシピ全体・任意）。加熱で水分が抜ける・油を吸うときに入れる */
  const [finished, setFinished] = useState(initial?.finishedGrams ? String(initial.finishedGrams) : "");
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<IngredientHit[]>([]);
  const [calc, setCalc] = useState<RecipeCalc | null>(null);
  const [fromEstimate, setFromEstimate] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seed = useMemo(() => estimateRows(estimate), [estimate]);
  // 写真から推定
  const [photo, setPhoto] = useState<{ base64: string; dataUri: string } | null>(null);
  const [hint, setHint] = useState("");
  const [consent, setConsent] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoResult, setPhotoResult] = useState<PhotoEstimate | null>(null);

  // 検索（打つたびに投げない — 止まってから）
  useEffect(() => {
    const q = query.trim();
    if (!q) return setHits([]);
    const t = setTimeout(() => {
      void api.searchIngredients(q).then((r) => r.ok && setHits(r.data));
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  // 計算（行・人前が変わって止まったら 1 回）
  const servingsN = Math.max(1, Math.min(200, Math.floor(Number(servings) || 1)));
  const items = rows.map((r) => ({ id: r.id, grams: toGrams(r.grams) }));
  const valid = items.length > 0 && items.every((i) => i.grams != null);
  const finishedN = finished.trim() ? toGrams(finished) : null;
  const key = JSON.stringify([servingsN, items, finishedN]);
  useEffect(() => {
    if (!valid) return setCalc(null);
    const t = setTimeout(() => {
      void api
        .recipeCalc({ name: entryName, servings: servingsN, items: items.map((i) => ({ id: i.id, grams: i.grams! })), finishedGrams: finishedN })
        .then((r) => {
          if (r.ok) {
            setCalc(r.data);
            setError(null);
          } else setError(r.error);
        });
    }, 300);
    return () => clearTimeout(t);
    // key が rows・servings の中身を表す
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, entryName]);

  const add = (hit: IngredientHit) => {
    const unit = hit.typicalUnits[0];
    setRows([...rows, { id: hit.id, name: hit.name, grams: unit ? String(unit.grams) : "" }]);
    setQuery("");
    setHits([]);
  };

  /** 推定から始める: 行を推定の食材構成で置き換える（名前は計算の応答で埋まる） */
  const startFrom = async (items: { id: string; grams: number }[]) => {
    const r = await api.recipeCalc({ name: entryName, servings: 1, items });
    if (!r.ok) return setError(r.error);
    setRows(r.data.items.map((i) => ({ id: i.id, name: i.name, grams: String(i.grams) })));
    setServings("1");
    setFromEstimate(true);
  };
  const startFromEstimate = () => startFrom(seed);

  const pickPhoto = async (file: File | undefined) => {
    setPhotoResult(null);
    setError(null);
    if (!file) return setPhoto(null);
    try {
      setPhoto(await toJpeg(file));
    } catch (e) {
      setPhoto(null);
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  /** 写真を送って推定 → 材料の行に置き換える（根拠は「推定」）。写真は kalori に残らない */
  const estimatePhoto = async () => {
    if (!photo || !consent) return;
    setPhotoBusy(true);
    setError(null);
    const r = await api.photoEstimate({ name: entryName, hint: hint.trim() || null, imageBase64: photo.base64, consent: true });
    setPhotoBusy(false);
    if (!r.ok) return setError(r.error);
    setPhotoResult(r.data);
    if (r.data.items.length > 0) await startFrom(r.data.items);
  };

  return (
    <details className="card recipe" open={initial != null}>
      <summary>
        <h2>
          材料から計算する <span className="badge trial" title="パイロット中は無料です。有料化・終了は 30 日前にお知らせします。">試用</span>
        </h2>
      </summary>
      <div className="stack">
        <p className="muted small">
          レシピの材料と重量を入れると、1 食あたりの栄養成分を計算します。計算結果は「入力欄に入れる」を押したときだけ上の欄に入ります。
          材料と重量は御社の機密として扱い、kalori のカタログやアプリには出しません。
        </p>
        <p className="muted small">
          ⚠️ ゆでる・揚げるなどの重量の変化は計算に入りません。調理後の重量なら調理後の食材（「ゆで」「焼き」など）を、生の重量なら生の食材を選んでください。
        </p>

        {editable && seed.length > 0 && rows.length === 0 && (
          <p>
            <button type="button" className="btn" onClick={() => void startFromEstimate()}>
              kalori の推定（材料と重量）から始める
            </button>{" "}
            <span className="muted small">推定の重量のままでは推定値です。御社のレシピの重量に直してお使いください。</span>
          </p>
        )}

        {editable && (
          <details className="photo-estimate">
            <summary>
              写真から推定する <span className="badge trial">試用</span>
            </summary>
            <div className="stack">
              <p className="muted small">
                料理の写真から、材料と重量の<strong>下書き</strong>を作ります。推定なので、重量を御社のレシピに合わせて直してからお使いください（値の根拠は「推定」になります）。アレルゲンは推定しません。
              </p>
              <div className="photo-pick">
                <input type="file" accept="image/jpeg,image/png,image/webp,image/heic" onChange={(e) => void pickPhoto(e.target.files?.[0])} aria-label="料理の写真" />
                {photo && <img src={photo.dataUri} alt="選んだ写真" className="photo-preview" />}
              </div>
              <label className="field">
                <span className="small">補足（任意。量・ソース・付け合わせなど）</span>
                <input value={hint} maxLength={500} onChange={(e) => setHint(e.target.value)} placeholder="例: 唐揚げ 5 個・ごはん 200g・タルタルソース付き" />
              </label>
              <label className="check-item">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
                <span className="small">
                  写真を推定のためだけに外部の生成 AI（Google Gemini）へ送ることに同意します。kalori は写真を保存しません。
                </span>
              </label>
              <p>
                <button type="button" className="btn" disabled={!photo || !consent || photoBusy} onClick={() => void estimatePhoto()}>
                  {photoBusy ? "推定しています…（数十秒かかります）" : "写真から推定する"}
                </button>
              </p>
              {photoResult && (
                <p className="notice small">
                  {photoResult.notFood
                    ? "料理の写真として読み取れませんでした。別の写真でお試しください。"
                    : photoResult.items.length > 0
                      ? `「${photoResult.recognizedFood}」と推定しました（信頼度 ${Math.round(photoResult.confidence * 100)}%）。下の材料と重量を、御社のレシピに合わせて直してください。`
                      : `「${photoResult.recognizedFood}」と推定しましたが、材料に分けられませんでした（参考: ${fmtNumber(photoResult.nutrition.cal, "kcal")}）。材料を下で入力してください。`}
                </p>
              )}
            </div>
          </details>
        )}

        <div className="toolbar">
          <label className="field inline-field">
            <span>このレシピで作る量</span>
            <span className="input-unit">
              <input inputMode="numeric" value={servings} disabled={!editable} onChange={(e) => setServings(e.target.value)} aria-label="人前" />
              人前
            </span>
          </label>
          <label className="field inline-field">
            <span>仕上がり重量（任意）</span>
            <span className="input-unit">
              <input
                inputMode="decimal"
                value={finished}
                disabled={!editable}
                aria-invalid={finished.trim() !== "" && finishedN == null}
                onChange={(e) => setFinished(e.target.value)}
                aria-label="仕上がり重量"
                placeholder="レシピ全体"
              />
              g
            </span>
          </label>
        </div>
        <p className="muted small">
          ゆでる・焼くで水分が抜ける・揚げて油を吸う料理は、できあがりのレシピ全体の重さを「仕上がり重量」に入れると、1 食の重量と 100g
          あたりが正しくなります（吸った油は材料に「油」として足してください）。
        </p>

        {rows.length > 0 && (
          <table className="table recipe-rows">
            <thead>
              <tr>
                <th>材料（成分表の食品・自社の食材と部品）</th>
                <th className="num">重量（レシピ全体）</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.id}-${i}`}>
                  <td>{r.name}</td>
                  <td className="num">
                    <span className="input-unit">
                      <input
                        inputMode="decimal"
                        value={r.grams}
                        disabled={!editable}
                        aria-invalid={toGrams(r.grams) == null}
                        aria-label={`${r.name} の重量`}
                        onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, grams: e.target.value } : x)))}
                      />
                      g
                    </span>
                  </td>
                  <td>
                    {editable && (
                      <button type="button" className="btn-link" onClick={() => setRows(rows.filter((_, j) => j !== i))}>
                        外す
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {editable && (
          <div className="ingredient-search">
            <input type="search" placeholder="材料を探す（例: 鶏もも、白飯、しょうゆ）" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="材料を探す" />
            {hits.length > 0 && (
              <ul className="hits">
                {hits.map((h) => (
                  <li key={h.id}>
                    <button type="button" onClick={() => add(h)}>
                      <span>{h.name}</span>
                      <span className="muted small">
                        {h.source === "own" ? "★ " : ""}
                        {h.category} ・ 100g {fmtNumber(h.per100g.cal, "kcal")}
                        {h.typicalUnits[0] ? ` ・ ${h.typicalUnits[0].label} ${h.typicalUnits[0].grams}g` : ""}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {error && <p className="error">{error}</p>}

        {calc && (
          <div className="stack">
            <table className="table kv">
              <tbody>
                <tr>
                  <th />
                  <th className="num">1 食</th>
                  <th className="num">100g あたり</th>
                </tr>
                {NUTRITION_FIELDS.map((f) => (
                  <tr key={f.key}>
                    <th>{f.label}</th>
                    <td className="num">{calc.perServing[f.key] == null ? "—" : fmtNumber(calc.perServing[f.key], f.unit)}</td>
                    <td className="num muted">{calc.per100g[f.key] == null ? "—" : fmtNumber(calc.per100g[f.key], f.unit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="muted small">
              1 食 {fmtNumber(calc.perServingGrams, "g")}（材料の合計 {fmtNumber(calc.totalGrams, "g")}
              {calc.finishedGrams != null && `・仕上がり ${fmtNumber(calc.finishedGrams, "g")}`}・{calc.servings} 人前）
              {Object.keys(calc.allergenCandidates).length > 0 &&
                ` ・ 自社の食材・部品のアレルゲン: ${Object.entries(calc.allergenCandidates)
                  .map(([k, v]) => `${k}${v === "may_contain" ? "（コンタミ）" : ""}`)
                  .join("・")}（入力欄に入れると、アレルゲンの候補に出ます）`}
              {calc.undermeasured.length > 0 && " ・ 「—」の項目は、成分表に値が無い材料があるため計算していません（入れると少なく見えるため）。"}
            </p>
            {editable && (
              <p>
                <button type="button" className="btn primary" onClick={() => onApply(calc, fromEstimate)}>
                  計算結果を入力欄に入れる
                </button>{" "}
                <span className="muted small">「—」の項目は入力欄を変えません。</span>
              </p>
            )}
          </div>
        )}
        <p className="muted small">
          ★ = 御社の食材・部品（市販品の規格書の値や、ごはん・ソースなどの部品は <Link href="/ingredients">自社の食材・部品</Link> で登録できます）
        </p>
        <p className="muted small">出典: 文部科学省「日本食品標準成分表（八訂）増補」をもとに kalori が計算</p>
      </div>
    </details>
  );
}
